import { Injectable } from '@angular/core';
import { MonthlyPayment, YearlyPayment, Prepayment, LoanSummary, ScheduleResult } from './models';

@Injectable({ providedIn: 'root' })
export class LoanCalculatorService {

  computeEMI(principal: number, annualRate: number, termYears: number): number {
    const r = annualRate / 100 / 12;
    const n = termYears * 12;
    if (r === 0) return principal / n;
    return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }

  buildSchedule(
    principal: number,
    annualRate: number,
    termYears: number,
    baseEMI: number,
    prepayments: Prepayment[],
    prepaymentOption: 'reduce_tenure' | 'reduce_emi',
    startDate: string
  ): ScheduleResult {
    const r = annualRate / 100 / 12;
    const originalN = termYears * 12;

    const prepayMap: Record<number, number> = {};
    prepayments.forEach(p => {
      prepayMap[p.month] = (prepayMap[p.month] ?? 0) + p.amount;
    });

    let balance = principal;
    let currentEMI = baseEMI;
    const months: MonthlyPayment[] = [];
    let totalInterest = 0;
    let totalPayment = 0;

    const [sY, sM] = startDate
      ? startDate.split('-').map(Number)
      : [new Date().getFullYear(), new Date().getMonth() + 1];

    for (let month = 1; balance > 0.5 && month <= originalN + 24; month++) {
      const offset = month - 1;
      const calendarYear = sY + Math.floor((sM - 1 + offset) / 12);
      const calendarMonth = (sM - 1 + offset) % 12;

      const interest = balance * r;
      let principal_ = currentEMI - interest;

      // Last payment clamp
      if (principal_ >= balance) {
        principal_ = balance;
        const lastEMI = principal_ + interest;
        totalInterest += interest;
        totalPayment += lastEMI;
        months.push({
          month, calendarYear, calendarMonth,
          emi: lastEMI, principal: principal_, interest,
          prepayment: prepayMap[month] ?? 0,
          balance: 0,
          totalPrincipalPaid: principal,
          totalInterestPaid: totalInterest
        });
        break;
      }

      balance -= principal_;

      // Apply prepayment (clamped to remaining balance)
      const prepayAmt = Math.min(prepayMap[month] ?? 0, balance);
      if (prepayAmt > 0) balance -= prepayAmt;

      totalInterest += interest;
      totalPayment += currentEMI + prepayAmt;

      months.push({
        month, calendarYear, calendarMonth,
        emi: currentEMI, principal: principal_, interest,
        prepayment: prepayAmt, balance,
        totalPrincipalPaid: principal - balance,
        totalInterestPaid: totalInterest
      });

      // Reduce EMI mode: recalculate after prepayment
      if (prepaymentOption === 'reduce_emi' && balance > 0) {
        const remainingN = originalN - month;
        if (remainingN > 0) {
          currentEMI = this.computeEMI(balance, annualRate, remainingN / 12);
        }
      }
    }

    return { months, totalInterest, totalPayment, finalEMI: currentEMI };
  }

  buildYearly(monthlySchedule: MonthlyPayment[]): YearlyPayment[] {
    const map: Record<number, YearlyPayment> = {};
    for (const m of monthlySchedule) {
      const y = m.calendarYear;
      if (!map[y]) {
        map[y] = { calendarYear: y, emi: 0, principal: 0, interest: 0, prepayment: 0, balance: 0 };
      }
      map[y].emi += m.emi;
      map[y].principal += m.principal;
      map[y].interest += m.interest;
      map[y].prepayment += m.prepayment;
      map[y].balance = m.balance;
    }
    return Object.values(map);
  }

  buildSummary(emi: number, result: ScheduleResult): LoanSummary {
    return {
      emi,
      totalInterest: result.totalInterest,
      totalPayment: result.totalPayment,
      totalMonths: result.months.length
    };
  }
}
